import type { Query } from "@cubejs-client/core";
import type {
  AverageLoads,
  ChannelScope,
  CubeQueryFailure,
  InsightsQueryResult,
  MetricCell,
  MetricColumn,
  MetricKey,
  ViewMeta,
} from "src/pages/ad-studio/insights/performance/types";
import {
  cubeLoad,
  useInsightsQueries,
  useInsightsQuery,
} from "src/pages/ad-studio/insights/performance/use-insights-query";
import type { Averages } from "src/pages/ad-studio/insights/performance/utils/metric-distribution";
import {
  aggregateOf,
  rowCountOf,
} from "src/pages/ad-studio/insights/performance/utils/query";

type Reporting = { key: string; scope: readonly ChannelScope[] };

const reportingOf = ({
  column,
  scope,
}: {
  column: MetricColumn;
  scope: readonly ChannelScope[];
}): Reporting => {
  const reporting = scope.filter(({ channel }) =>
    column.channels.includes(channel.sourceType),
  );
  return {
    key: reporting.map(({ channel }) => channel.sourceType).join(","),
    scope: reporting,
  };
};

const perRowReportingOf = ({
  metrics,
  scope,
}: {
  metrics: readonly MetricColumn[];
  scope: readonly ChannelScope[];
}): readonly Reporting[] => [
  ...new Map(
    metrics
      .filter((column) => column.average === "per-row")
      .map((column) => reportingOf({ column, scope }))
      .filter((reporting) => reporting.scope.length > 0)
      .map((reporting): [string, Reporting] => [reporting.key, reporting]),
  ).values(),
];

type RowCounts =
  | { status: "pending" }
  | { status: "failed" }
  | { status: "loaded"; byChannels: ReadonlyMap<string, number> };

const toRowCounts = ({
  channels,
  counts,
}: {
  channels: readonly string[];
  counts: readonly InsightsQueryResult<number, CubeQueryFailure>[];
}): RowCounts => {
  if (counts.some(({ status }) => status === "failed")) {
    return { status: "failed" };
  }
  const loaded = channels.flatMap((key, index): [string, number][] => {
    const count = counts[index];
    return count?.status === "loaded" ? [[key, count.value]] : [];
  });
  return loaded.length < channels.length
    ? { status: "pending" }
    : { status: "loaded", byChannels: new Map(loaded) };
};

const toAverages = ({
  cells,
  byChannels,
  scope,
}: {
  cells: readonly MetricCell[];
  byChannels: ReadonlyMap<string, number>;
  scope: readonly ChannelScope[];
}): Averages => ({
  kind: "aggregate",
  values: new Map(
    cells.flatMap(({ column, value }): [MetricKey, number][] => {
      if (typeof value !== "number") {
        return [];
      }
      if (column.average !== "per-row") {
        return [[column.key, value]];
      }
      const count = byChannels.get(reportingOf({ column, scope }).key);
      return count === undefined || count === 0
        ? []
        : [[column.key, value / count]];
    }),
  ),
});

const cubeAverageLoads = ({
  list,
  view,
  metrics,
  scope,
}: {
  list: Query;
  view: ViewMeta;
  metrics: readonly MetricColumn[];
  scope: readonly ChannelScope[];
}): AverageLoads => ({
  aggregate: cubeLoad({
    cube: aggregateOf({ list, view, metrics }),
    wire: view.wire,
  }),
  rowCounts: perRowReportingOf({ metrics, scope }).map((reporting) => ({
    channels: reporting.key,
    load: cubeLoad({
      cube: rowCountOf({ list, view, scope: reporting.scope }),
      wire: view.wire,
    }),
  })),
});

/** "vs average" compares a row to every row in scope; a summed metric's average is per row from the channels that report it. */
export function useAverages({
  list,
  view,
  metrics,
  scope,
}: {
  list: Query;
  view: ViewMeta;
  metrics: readonly MetricColumn[];
  scope: readonly ChannelScope[];
}): Averages {
  const loads = cubeAverageLoads({ list, view, metrics, scope });
  const aggregate = useInsightsQuery(loads.aggregate);
  const counts = toRowCounts({
    channels: loads.rowCounts.map(({ channels }) => channels),
    counts: useInsightsQueries(loads.rowCounts.map(({ load }) => load)),
  });
  switch (aggregate.status) {
    case "loading": {
      return { kind: "aggregate-pending" };
    }
    case "failed": {
      return { kind: "no-aggregate" };
    }
    case "loaded": {
      const found = aggregate.value;
      if (counts.status === "pending") {
        return { kind: "aggregate-pending" };
      }
      if (found.kind === "not-found" || counts.status === "failed") {
        return { kind: "no-aggregate" };
      }
      return toAverages({
        cells: found.value,
        byChannels: counts.byChannels,
        scope,
      });
    }
    default: {
      return aggregate satisfies never;
    }
  }
}
